/**
 * How to reach a person.
 *
 * **There is no form, and that is a decision rather than a shortcut.** This site
 * is static files on nginx (ADR-0033) with no backend of its own, so a form
 * would need an endpoint, a spam story, and somewhere for the message to go when
 * that endpoint is down — and the failure mode of a form nobody is receiving is
 * a visitor who believes they have been in touch. A `mailto:` with the subject
 * already written opens a client the reader already has, reaches an inbox that
 * already exists, and leaves the sent copy in their own outbox as proof they
 * wrote. The telephone number beside it is the same argument at a shorter
 * timescale.
 *
 * The three cards are ordered by how quickly they answer: telephone, mail,
 * door. The address is last because it is the one nobody needs today and the one
 * a procurement department needs exactly once.
 */
import { Reveal, Stagger } from '../kit'
import { useT } from '../i18n/context'
import { COMPANY, demoMailto } from '../data/links'
import { PageHeader } from '../sections/PageHeader'

export function ContactPage() {
  const t = useT()

  return (
    <>
      <PageHeader
        eyebrow="contactPage.eyebrow"
        title="contactPage.title"
        sub="contactPage.sub"
        toc={[]}
      />

      <section className="kl-section kl-section--sunken" aria-labelledby="reach-title">
        <div className="kl-inner">
          <h2 id="reach-title" className="k-visually-hidden">
            {t('contact.reach.title')}
          </h2>

          <Stagger className="kl-contactgrid" as="ul" childAs="li" step={0.07}>
            <div className="kl-contactcard">
              <p className="kl-contactcard__key">{t('contact.phone.label')}</p>
              <a className="kl-contactcard__value k-mono" href={COMPANY.phoneHref}>
                {COMPANY.phone}
              </a>
              <p className="kl-contactcard__note">{t('contact.phone.note')}</p>
            </div>

            <div className="kl-contactcard">
              <p className="kl-contactcard__key">{t('contact.email.label')}</p>
              <a className="kl-contactcard__value" href={`mailto:${COMPANY.email}`}>
                {COMPANY.email}
              </a>
              <p className="kl-contactcard__note">{t('contact.email.note')}</p>
            </div>

            <div className="kl-contactcard">
              <p className="kl-contactcard__key">{t('contact.address.label')}</p>
              {/* `address` is the element for the contact details of its nearest
                  article or document, which is exactly what this is. */}
              <address className="kl-contactcard__value kl-contactcard__value--address">
                {COMPANY.legalName}
                <br />
                {COMPANY.address}
                <br />
                {COMPANY.district} · {COMPANY.country}
              </address>
              <p className="kl-contactcard__note">{t('contact.address.note')}</p>
            </div>
          </Stagger>
        </div>
      </section>

      <section className="kl-section kl-demo" aria-labelledby="demo-title">
        <div className="kl-inner">
          <Reveal className="kl-demo__card k-frame">
            <p className="k-eyebrow">
              <span className="k-label">{t('contact.demo.eyebrow')}</span>
            </p>
            <h2 id="demo-title" className="kl-demo__title">
              {t('contact.demo.title')}
            </h2>
            <p className="kl-demo__sub">{t('contact.demo.sub')}</p>

            {/* What a first conversation covers. Written down because the most
                common reason a demo request goes unsent is not knowing what one
                is agreeing to. */}
            <ul className="kl-demo__list">
              <li>{t('contact.demo.item.1')}</li>
              <li>{t('contact.demo.item.2')}</li>
              <li>{t('contact.demo.item.3')}</li>
            </ul>

            <div className="kl-demo__actions">
              <a
                className="kl-btn kl-btn--primary"
                href={demoMailto(t('contact.demo.subject'))}
              >
                {t('contact.demo.cta')}
              </a>
              <a className="kl-btn" href={COMPANY.phoneHref}>
                {t('contact.demo.call')}
              </a>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  )
}
